// ============================================================
// MANIC MEETER — English content: colleagues
// ============================================================
// Catchphrases only. The first names live in ../names.ts — they are the same
// in both languages, because the office has not moved.

import { ColleagueRole } from "../../types";

export const CATCH_PHRASES: Record<Exclude<ColleagueRole, ColleagueRole.Sprachbarriere>, string[]> = {
  [ColleagueRole.FrontendDev]: [
    "So, the pixels are 2 off...",
    "Couldn't we just do this in React?",
    "It looks completely different in dark mode!",
    "That button has 1px too much padding.",
    "Responsiveness isn't a feature, it's an obligation!",
    "Let me just clear the cache...",
    "Chrome DevTools says otherwise.",
    "Could we please use web components?",
    "The CSS grid isn't aligned.",
    "We absolutely need a design system library.",
    "The z-index is completely broken.",
    "Mobile first, people. Mobile first.",
    "The Lighthouse score has dropped below 80.",
    "I found an accessibility issue...",
    "Can we make that dropdown custom, please?",
    "TypeScript throws an error here, but it works anyway.",
    "The layout breaks at 320px wide.",
    "We should be tracking bundle size.",
    "Should we bring in Tailwind? Or maybe not?",
    "Safari renders it completely differently.",
    "npm install found 847 vulnerabilities. As usual.",
    "Why has somebody used !important? Forty-three times?",
    "Dark mode now has its own dark mode.",
    "I spent three hours on a CSS bug. It was a semicolon.",
    "It's perfect on my localhost. Production? Well.",
    "Can we please stop using jQuery? It's 2026.",
    "The console shows 200 deprecation warnings. That's normal.",
    "We have three different state management solutions. All at once.",
    "The Figma export uses fonts we haven't licensed.",
    "I've rebuilt that button five times now. Which version was it again?",
    "The animation lags on an iPhone 8. Does anyone else still have one?",
    "Can we finally do SSR? Or CSR? Or ISR? Or... whichever?",
    "Why did the designer use 47 different shades of grey?",
    "My hot reload stopped working. Ever since the last npm update.",
    "IE11 support has been officially dropped. I am celebrating.",
    "The font file is 4MB. Do we really need Cyrillic?",
    "The client says the site is slow. He's on Edge. On Windows 7.",
    "We have a monorepo now. Well, three monorepos.",
    "The Storybook instance takes twelve minutes to start.",
    "I solved it in CSS. Yes, the business logic too.",
    "Web vitals are red. But only on staging. Probably.",
    "Our design token system has more tokens than Pantone has colours.",
    "The pull request changed 847 files. It was a linter fix.",
    "Why does Firefox render flexbox gap differently? Why?!",
    "We have fourteen button variants. The designer wants a fifteenth.",
    "My Docker container for the frontend toolchain needs 8GB of RAM.",
    "The AI generated 200 lines of CSS for me. I need two of them.",
    "Copilot has been suggesting the same broken function since this morning.",
    "I asked the AI to explain the error. It apologised and then repeated it.",
    "The designs come out of the AI now. They look like everything else.",
    "The AI recommended me a framework that does not exist.",
    "I refactored the generated code. One semicolon survived.",
  ],
  [ColleagueRole.BackendDev]: [
    "That's a feature, not a bug.",
    "It runs on my machine...",
    "Microservices solve that problem.",
    "The API is RESTful, that's enough.",
    "We need a cache in front of it.",
    "Do we have tests for that? No? Cool.",
    "The database is the bottleneck, not my code.",
    "Docker solves that.",
    "I'll just put a quick fix into production.",
    "I'm not touching the legacy codebase.",
    "The logging is perfectly adequate.",
    "We should migrate to Kubernetes.",
    "The latency is within acceptable bounds.",
    "We'll refactor that in sprint 47.",
    "The endpoint is documented. Somewhere in the wiki.",
    "We need more RAM on the server.",
    "CORS is not a backend problem.",
    "The migration is theoretically backwards compatible.",
    "Git blame says it was the intern.",
    "The queue is full, but it'll sort itself out.",
    "I pushed the fix straight to main. What's the worst that can happen.",
    "The database has 4 million orphaned rows. Doesn't bother anything.",
    "We have 97% code coverage. The other 3% is the important part.",
    "That cron job has been running since 2019. Nobody knows what it does.",
    "I restarted the server. Problem solved.",
    "That's a known issue. For three years.",
    "The env variables are in the README. In theory.",
    "We have a race condition. But only under load. So, always.",
    "The health check says green. The service is dead anyway.",
    "I found the bug. It was an encoding problem. Obviously.",
    "The API returns 200. The body says 'error'. Design decision.",
    "We need a service mesh. No, I can't explain why.",
    "The database index is missing. For two years. Performance was 'acceptable'.",
    "I committed the secret to the repo. Briefly. For three seconds.",
    "The migration took four hours. On a Friday. At 5pm.",
    "We have 73 microservices. Four of them do something. The rest 'might be useful'.",
    "The webhook fired 3 million times. Per hour. That was not a feature.",
    "We store passwords properly now. Since last week.",
    "The load balancer doesn't balance. It sends everything to server 3.",
    "We've fixed the same bug three times. It keeps coming back.",
    "The logs have grown to 500GB. In one week.",
    "I wrote a bash script. It is now business critical.",
    "The Terraform state file is corrupt. Again.",
    "We deploy by copy-pasting onto the server. Agile.",
    "The retry mechanism DDoS'd the server. Our own server.",
    "I wrote the documentation. In my head. I am the documentation.",
    "The AI wanted to open my migration with DROP TABLE.",
    "I write the tests with AI now. They test that true is true.",
    "I spent three hours hunting a bug the AI introduced in four seconds.",
    "The model knows our API better than our docs do. That says more about the docs.",
    "The agent wrote 200 lines of migration. I deleted 200 lines.",
    "The AI knows every design pattern. It applied all of them at once.",
  ],
  [ColleagueRole.ClientManager]: [
    "But the client wants it by yesterday!",
    "Could we approach that agilely?",
    "The stakeholder needs visibility!",
    "We have to communicate proactively here.",
    "The expectation management is off.",
    "The client understood it differently.",
    "We need an executive summary.",
    "The deadline is not negotiable.",
    "Could we deliver a quick win there?",
    "There's no alignment with the client.",
    "The customer experience has to be right.",
    "We need to nurture the client relationship.",
    "That can't go into the review like that.",
    "The client pays, the client decides.",
    "We need a status update for the client.",
    "Can we settle that in an escalation round?",
    "The purchase order still isn't through.",
    "The main contractor has other ideas about that.",
    "We should set up a Jour Fixe with the client.",
    "The proposal has to go out by Friday.",
    "The client has a 'small change'. In quotation marks.",
    "We need to explain scope creep to the client. Diplomatically.",
    "The final costing is going to be... interesting.",
    "The client hasn't read the contract. But he has expectations.",
    "I'll tell the client it's possible. You'll make it possible, yes?",
    "The proposal is out. Including three things we cannot do.",
    "The client is calling. Yes, at 5:30 on a Friday too.",
    "The change request form is fourteen pages. The client wants it by tomorrow.",
    "The client says 'minimal'. He means 'completely new'. As always.",
    "We gave the client two options. He wants option three.",
    "Sign-off is in an hour. The client changed the requirements. Five minutes ago.",
    "The client mistook the prototype for the finished product. Oops.",
    "We need an NDA. For the NDA. Yes, really.",
    "The client heard 'agile' and thinks it means 'faster and cheaper'.",
    "The feedback arrived as 47 separate emails. Within ten minutes.",
    "Our contact at the client has changed. For the third time. In one month.",
    "I told the client 'this is complex'. He heard 'it'll be ready Friday'.",
    "The client wants the design in Comic Sans. I wish I were joking.",
    "The project was scoped at six months. We are in month fourteen.",
    "The client has had an idea. It is always bad when the client has an idea.",
    "We took on a fixed-price project. The scope has tripled.",
    "The client wants a report. About the reports. Reportception.",
    "I've been on the phone with the client for three hours. He is still describing the problem.",
    "The contract says 'best effort'. The client hears 'guarantee'.",
    "We hit the milestone! The client moved the milestone.",
    "The client would like 'just one small thing'. It is never one small thing.",
    "The client wants 'something with AI'. It never got more specific than that.",
    "I sent the client the AI summary. He had it summarised by AI.",
    "'Are you doing this with AI?' — Yes. — 'So does it cost less?' — No.",
    "The client asked whether the AI could write the proposal too. It already has.",
    "The client forwarded our AI answer to their AI. We're awaiting the reply.",
    "'Is there actually a human on this?' — Situationally.",
  ],
  [ColleagueRole.HRManager]: [
    "We're like a family here!",
    "That's what it says in the culture handbook.",
    "Employer branding is key!",
    "Employee satisfaction is priority one.",
    "We've got a workshop planned for that.",
    "The onboarding needs optimising.",
    "The diversity ratio still isn't where it should be.",
    "We need a retention programme.",
    "A feedback culture starts with each of us.",
    "I'll raise that at the next town hall.",
    "We're launching a new benefits platform.",
    "The employee experience has to get better.",
    "We'll pick that up in the People & Culture meeting.",
    "Who still hasn't done the compliance training?",
    "We need more team-building events.",
    "Work-life balance mustn't suffer.",
    "The new purpose workshop will sort that out.",
    "The skills shortage is our problem too.",
    "Could we set up a mentoring programme for that?",
    "Internal communication needs to be more transparent.",
    "We ran an anonymous survey. Three people took part.",
    "The fruit basket counts as a benefit, right?",
    "We have a feel-good manager now. Do you feel better yet?",
    "The resignation came as a surprise. To us, anyway.",
    "New Work doesn't mean you don't have to come in.",
    "The appraisal is voluntary. But please come anyway.",
    "We have a new time-tracking tool. The fifth this year.",
    "The diversity target is 50/50. We're at 94/6. But we have a plan.",
    "The job ad has been up for eight months. We're selective.",
    "The feel-good manager has resigned. Because of the atmosphere.",
    "We have a new org chart. It contradicts last month's.",
    "Sick notes are piling up. Mostly Mondays and Fridays. Coincidence?",
    "Our home-office agreement allows two days. Some interpret that creatively.",
    "We're looking for a team lead. Nobody internal will volunteer.",
    "The 'Managing Stress' workshop was cancelled due to stress.",
    "The feedback session was very constructive. That is: I talked, he nodded.",
    "The salary round is transparent. Transparent means everyone knows there's nothing.",
    "We have a new data protection policy. 87 pages. Optional reading.",
    "The assessment centre had six candidates. Five cancelled. So did the sixth.",
    "The works council has objections. The works council always has objections.",
    "We offer mental health days now. One maximum. Per year.",
    "The employer branding campaign features people who don't work here.",
    "We introduced a buddy system. Three buddies have already resigned.",
    "The purpose workshop concluded that our purpose is unclear.",
    "The employee survey had 12% participation. We're calling it 'representative'.",
    "The offboarding was so good the employee nearly stayed. Nearly.",
    "We have an AI policy now. It was written by an AI.",
    "The applications are AI-generated and so are our rejections. Everyone saves time.",
    "I've read 400 cover letters. They all begin with 'I read with great interest'.",
    "We now train AI literacy. The training is a video from 2023.",
    "We're looking for five years' experience with a two-year-old model.",
    "The AI screened the candidate out. Reasoning to follow, once we understand it.",
  ],
  [ColleagueRole.MarketingGuru]: [
    "We need to leverage the content!",
    "Optimise the ROI of the synergy funnel!",
    "Touchpoints along the customer journey!",
    "Our reach on LinkedIn is up!",
    "We need more thought leadership.",
    "The funnel isn't converting well enough.",
    "Could we start a podcast?",
    "This has to go viral!",
    "We need an influencer collaboration.",
    "Brand awareness is still too low.",
    "Content is king, distribution is queen!",
    "We have to push the organic reach.",
    "The newsletter has a 0.3% click rate. That's fine.",
    "Could we do a whitepaper on that?",
    "The campaign needs more storytelling.",
    "We need to sharpen the target audience.",
    "Growth hacking is the way forward.",
    "The social media strategy is in place. In theory.",
    "Paid versus organic — the eternal question.",
    "We need user-generated content.",
    "Our TikTok has twelve followers. Four are from the team.",
    "The landing page still needs a hero shot. And a video. And testimonials.",
    "We're disrupting the market! With a blog post.",
    "The marketing-qualified lead turned out to be the intern's mother.",
    "Could we let ChatGPT write our posts? Oh, we already do?",
    "The rebrand took six months. The new blue is 2% different.",
    "Our email campaign set a new unsubscribe record!",
    "We created a hashtag. It isn't trending. But we use it internally.",
    "The press release was rewritten three times. Nobody read it.",
    "We sponsor a podcast now. Forty-seven listeners. Forty-six from marketing.",
    "The SEO consultant says we rank on page eight. For our own company name.",
    "We ran a guerrilla marketing stunt. The police were not delighted.",
    "The webinar had 200 registrations. Three showed up. One was me.",
    "Our trade fair stand is twice the size of last year's. So is the budget.",
    "The A/B test has been running six months. Result: A and B are equally bad.",
    "We built a customer persona. Her name is Marketing Manuela.",
    "A competitor copied our design. I am flattered and furious.",
    "We're doing employer branding now. Step one: stock photos of smiling people.",
    "The QR code on the poster leads to the wrong page. For three months.",
    "Our brand voice guidelines document is 60 pages. Nobody has read it.",
    "We started a meme account. Legal shut it down immediately.",
    "The print ad has a typo. Ten thousand copies are already printed.",
    "The influencer tagged us. He has 200 followers. 150 are bots.",
    "We need 'more engagement'. Nobody knows exactly what that means.",
    "The marketing budget was cut. The targets were doubled. Naturally.",
    "We have a brand ambassador now. It's the intern.",
    "As of today we are AI-powered. Technically nothing has changed.",
    "The model in the campaign has three arms, but the reach is good.",
    "I had sixty slogans generated. We're using the one I already had.",
    "'AI-powered' lifts the click rate by 12%. Regardless of what it's on.",
    "We generate 40 posts a week now. Nobody reads any of them.",
    "The audience analysis is from the AI. By now, so is the audience.",
  ],
  [ColleagueRole.Chef]: [
    "My calendar is packed, so briefly...",
    "This is strategically important for Q3!",
    "We need to show ownership!",
    "The vision has to be communicated clearly.",
    "I expect more initiative here.",
    "Next year's budget isn't fixed yet.",
    "We have to become more efficient. Full stop.",
    "We'll escalate that to the board if we have to.",
    "That's what I promised the CEO.",
    "Performance is not a nice-to-have.",
    "We need more execution, less planning.",
    "The numbers speak for themselves.",
    "That's a top-down decision.",
    "I want to see results here. Soon.",
    "Our competition isn't sleeping.",
    "The shareholders expect growth.",
    "Lead by example, people.",
    "We are leaving no stone unturned.",
    "The next town hall is going to be a difficult one.",
    "I don't want excuses, I want solutions.",
    "I have an open door policy! Appointments via my assistant, please.",
    "We have to leave our comfort zone. I'll start tomorrow.",
    "Think like entrepreneurs! But ask me first.",
    "The transformation begins with each of us. Except me.",
    "I was at a conference yesterday. We need AI. Everywhere.",
    "Lunch with the investor was productive. We had lobster.",
    "I'm delegating that. Which means you're doing it.",
    "My golf partner says AI is the future. He sells insurance.",
    "I just read a very inspiring book. I've forgotten the title.",
    "The strategy is settled. It only changes weekly now.",
    "I've handled far worse crises in my career. Allegedly.",
    "Town hall is mandatory for everyone. I'll be twenty minutes late. As always.",
    "You're my best team. I say that to all my teams.",
    "We have to think disruptively! But within compliance, please.",
    "I'm at my next appointment in an hour. In Dubai. By video, obviously.",
    "Cut costs and raise quality at the same time. It's only logical.",
    "My vision? It's on the intranet. Page 47. Footnote 3.",
    "We're a start-up in spirit. With 3,000 employees.",
    "The quarterly report goes out today. The numbers are... creatively presented.",
    "I need the result in a PowerPoint. Three slides maximum. With every detail.",
    "Our competitive advantage? That we still exist.",
    "I don't make mistakes. I make 'learnings'.",
    "This reorganisation will work. The seventh is the charm.",
    "Team dinner tonight. It's voluntary. But I notice who doesn't come.",
    "I'm approachable. Just send me an email. My assistant forwards them.",
    "The bonus is smaller this year. Not mine. Yours.",
    "We have to think AI-first now. Don't ask me what comes after that.",
    "I had the AI review our strategy. It found it 'ambitious'.",
    "AI saves us 30% of our time. We fill that with meetings about the AI.",
    "Nobody is being replaced by AI. I'll have the AI confirm that in writing.",
    "I asked the AI whether we have too many meetings. It says yes. Let's discuss it.",
    "The efficiency gains are considerable. We have not been able to evidence them yet.",
  ],
  [ColleagueRole.Abteilungsleiter]: [
    "In my department we do that differently.",
    "I'm escalating that upwards.",
    "We need more headcount!",
    "The prioritisation has to come from above.",
    "My team is at its limit.",
    "We discussed that in the departmental meeting.",
    "I don't have sign-off from the management board for that.",
    "Work cross-functionally? Happily, but...",
    "The budget is never enough.",
    "The other department is blocking us.",
    "We need clear responsibilities.",
    "It has always been done that way in my area.",
    "The resource planning is wrong from every angle.",
    "Could we bring in external support?",
    "The reporting has to be finished by Friday.",
    "We need to standardise the processes.",
    "My people are already doing overtime.",
    "That does not fall within my remit.",
    "I'll raise it at the management meeting.",
    "The numbers from my department are solid.",
    "Why does the other department have more budget? Because they shout louder.",
    "My team has lost three people. Replacements are coming 'soon'.",
    "The resource planning spreadsheet has 47 tabs.",
    "We have a silo problem. But my silo is well organised.",
    "I told the boss it can't be done. He says it must.",
    "The reorg doesn't affect us. Probably. Hopefully.",
    "My department has the best coffee machine. That is not an accident.",
    "The other department has three team leads. We have half of one.",
    "I wrote a twelve-page report. Nobody read it. As always.",
    "The org chart puts me above marketing. Marketing sees it differently.",
    "We wrote a strategy paper. It contradicts last quarter's.",
    "My predecessor introduced processes. I abolished them all. And introduced new ones.",
    "The KPIs are green. Because I adjusted the thresholds.",
    "We need another team lead. The current one is... overextended.",
    "The management offsite was productive. We were at a spa.",
    "I raised it in the leadership circle. It was deferred. For the third time.",
    "My deputy handles this when I'm on leave. He just doesn't know yet.",
    "The restructuring only affects the others. I hope.",
    "We have OKRs now. On top of the KPIs. And the balanced scorecard. And the roadmap.",
    "I sit on fourteen steering committees. I steer nothing on any of them.",
    "The objectives are signed off. The objectives are 'ambitious'.",
    "My department has the fewest complaints. Because nobody knows where to complain.",
    "We're doing agile now. The others are, anyway. I'm still doing waterfall.",
    "The budget has been divided fairly. Fairly from my perspective.",
    "I resolved the conflict between team A and team B. Now they both hate me.",
    "The board presentation is finished. Eighty slides. For a ten-minute slot.",
    "I'd like an AI maturity report. By Friday. From the AI.",
    "We need an AI roadmap. And a roadmap for the AI roadmap.",
    "The numbers in the report are AI-generated and so is the commentary. So it's consistent.",
    "I've founded a task force. It is evaluating whether we need a task force.",
    "We are in the pilot phase. For fourteen months.",
    "The AI initiative has its own steering committee. It meets weekly.",
  ],
  [ColleagueRole.Teamleiter]: [
    "We'll discuss that at the next standup.",
    "I've logged it in Jira.",
    "Please stick to the timebox!",
    "Who's taking this ticket?",
    "The velocity isn't right.",
    "We have too much work in progress.",
    "Daily is at 9, not 9:15.",
    "Can we read that off the board?",
    "That blocks our sprint goal.",
    "I'll run a refinement later.",
    "We need clearer acceptance criteria.",
    "The burndown doesn't look good.",
    "No side quests in the sprint, please.",
    "Who reviewed the pull request?",
    "The retro action items are still open.",
    "That's out of scope for this sprint.",
    "We have to honour the definition of done.",
    "Pair programming would make sense here.",
    "Code review by this evening, please.",
    "I'll raise a blocker ticket right away.",
    "The standup is for status, not discussion! *discusses for 20 minutes*",
    "We completed three story points. Out of forty.",
    "I've created a new Jira label: 'not my problem'.",
    "Confluence is up to date. As of 2023.",
    "We need a meeting to reduce the number of meetings.",
    "The sprint is called 'Sprint 94 – this time we'll make it'.",
    "I escalated the blocker. Twice. Nobody replied.",
    "The code review queue has 47 open PRs. Some are from last year.",
    "I invented a new retro format: 'Whose fault was it?'.",
    "The sprint is broken. Not the code. The sprint itself.",
    "Pair programming is mandatory now. Nobody can find a partner.",
    "My kanban board has so many columns it needs a scrollbar.",
    "The user story fits on a post-it. The comment thread runs to three pages.",
    "I track team velocity in a spreadsheet. Irony? What irony?",
    "I organised the team event. Bowling. Everyone hates bowling. I know.",
    "The sprint review lasted five minutes. We had nothing to show.",
    "I wrote the retro outcomes into a ticket. It's in the backlog now. Page 47.",
    "We have twelve open epics. Eleven are 'in progress'. Since Q1.",
    "Sprint planning took four hours. For a two-week sprint.",
    "My favourite sentence: 'we'll discuss that at the next daily'. Spoiler: never.",
    "The tech debt backlog is bigger than the feature backlog. Considerably.",
    "I built a Jira workflow with 23 status transitions. Efficient.",
    "The release is tomorrow. The tests are still running. Some of them successfully.",
    "We do Scrum by the book. The book is from 2005.",
    "The team survey says 8/10 satisfaction. Two out of ten people responded.",
    "I cancelled a meeting! And created three new ones. Net negative, as always.",
    "Use AI by all means, but check everything. So: write it yourself.",
    "The AI notetaker assigned me tasks I never agreed to.",
    "From now on we measure AI usage per head. Yes, I know.",
    "I had the AI estimate your ticket. Two days. It always says two days.",
    "Please label AI-generated text. Yes, this email too.",
    "The estimate came from the model. The accountability stays with you.",
  ],
  [ColleagueRole.Designer]: [
    "The spacing is wrong.",
    "Figma is the source of truth!",
    "Could we run a design sprint?",
    "The colour palette isn't WCAG compliant.",
    "We need user research.",
    "The wireframe hasn't been signed off yet.",
    "Pixel perfect, please.",
    "The prototype is almost finished. Almost.",
    "Could we do a heuristic evaluation?",
    "The user journey has to flow better.",
    "Icons must not be inconsistent.",
    "We need more whitespace.",
    "The design system needs an update.",
    "The colour is #FF5733, not #FF5734!",
    "UX is not decoration.",
    "Can we A/B test which button works better?",
    "The micro-interactions are still missing.",
    "I need feedback from the user testing.",
    "The hover state design is still open.",
    "We should rethink the information architecture.",
    "The client wants Comic Sans. I quit.",
    "Those are not our brand colours. That is just red.",
    "I made three variants. The boss wants a fourth.",
    "At 16px that icon looks like an accident.",
    "We now have fourteen different blues in the product.",
    "The developer implemented my design. I do not recognise it.",
    "Can we shorten the copy? It is destroying my layout.",
    "The mockup is done. The mockup of the mockup, that is. The real one comes next week.",
    "I need a week for the design. 'Why so long?' Because iterations.",
    "The client wants it 'modern'. What is 'modern'? Rounded corners. Always rounded corners.",
    "My Figma project has 340 frames. Even I have lost track.",
    "The accessibility audit found 200 issues. Let's start with the colours.",
    "The developer turned my padding into margin. I feel it physically.",
    "We have a design language now. Nobody speaks it.",
    "User testing found that nobody can locate the login button. It is in the middle.",
    "I worked three hours on an icon. It is 16 by 16 pixels.",
    "The typeface costs €3,000 a year. The client wants Arial. Always Arial.",
    "The prototype works perfectly. In Figma. Reality is... different.",
    "We A/B tested it. Result: both variants are bad.",
    "My portfolio has 200 projects. In 180 of them my design was ignored.",
    "Dark mode looks better than light mode. Naturally everyone wants light mode.",
    "I updated the personas. Marketing Manuela is now AI Annika.",
    "The moodboard is approved. The board thought it was an art project.",
    "We have a design system with 500 components. Three get used.",
    "The client says 'less is more'. Then adds seven call-to-action buttons.",
    "My favourite question: 'could you make it a bit prettier?'",
    "The AI generated eighty variants. They all look like a startup from 2021.",
    "No AI images in the pitch, please. You can see the hands.",
    "The client had our logo quickly regenerated. It is now almost our logo.",
    "I generated the moodboard. Now the client wants exactly that — artefacts included.",
    "The prompt was 'clean and modern'. What I got was purple gradients.",
    "The AI can do everything except whitespace.",
  ],
  [ColleagueRole.Praktikant]: [
    "Er, should I take the minutes?",
    "We learned it differently at university...",
    "Could somebody give me access to the wiki?",
    "Where is the printer again?",
    "Sorry, I was still in the other meeting.",
    "Am I allowed to say something too?",
    "I googled it, but...",
    "Is this a meeting or a workshop?",
    "My VPN access still doesn't work.",
    "Do I have to log that in Jira as well?",
    "When is lunch around here?",
    "Er, what does 'EOB' mean?",
    "Should I document that in Confluence?",
    "I've forgotten my password...",
    "My contract only runs until next month.",
    "Could somebody explain Git to me?",
    "Is Friday really casual here?",
    "Nobody told me there was a dress code.",
    "I don't know the Jira workflow that well yet.",
    "Sorry, I have another stupid question...",
    "I accidentally pushed to main. Is that bad?",
    "My dissertation topic is now this project. Help.",
    "May I leave at four? I have lectures.",
    "I mistook the production server for the test server...",
    "Do you get a second monitor here?",
    "I asked ChatGPT and it says you're doing this wrong.",
    "Can I put my mother down as a reference?",
    "I named the Git branch 'yolo-deploy'. Was that wrong?",
    "My screen now shows only blue text on a blue background. Help.",
    "Am I allowed to talk about my cat at the daily?",
    "I opened a pull request. With 14,000 lines. Is that a lot?",
    "Can you use Jira on a phone? Asking for a friend.",
    "I googled unit testing. Sounds like effort. Is it compulsory?",
    "Should I fix the bugs or just document them?",
    "I posted the API key on Twitter. Is that bad?",
    "Sorry, but what is a sprint? Are we running?",
    "My laptop has four stickers. Am I a real developer now?",
    "I wrote 'TODO: fix later' in the code. That counts as documentation, right?",
    "The coffee machine on the third floor makes a better cappuccino. Just so you know.",
    "I don't know what a merge conflict is, but I appear to have created one.",
    "My mentor told me to 'read more'. He means documentation, right?",
    "Could we move the meeting to after ten? I'm not a morning person.",
    "I prepared a presentation. In Comic Sans. Ironically. I think.",
    "My placement report is three pages now. About the coffee machine.",
    "I restarted the server. The wrong one. I'm sorry.",
    "Who exactly is this 'Kubernetes' and why does everyone talk about him?",
    "I wrote the docs with AI. It was scheduled as a two-week task.",
    "May I ask why we're reading the AI's answer aloud in a meeting?",
    "I asked the AI what an alignment call is. It wasn't quite sure either.",
    "Can the AI write my placement report too? ...Asking for myself.",
    "I'm supposed to check the AI's output. I've been here two weeks.",
    "If the AI does the tasks — what am I learning?",
  ],
  [ColleagueRole.ScrumMaster]: [
    "How does the team feel about that?",
    "Car park that one, please!",
    "Let's timebox this to two minutes!",
    "That's an impediment, I'll clear it.",
    "How high is your confidence?",
    "The daily is for synchronising, not discussing.",
    "Who is facilitating the next meeting?",
    "We need psychological safety.",
    "Let's run a fishbowl discussion.",
    "The sprint velocity is still normalising.",
    "Could we track that on a kanban board?",
    "I'll run a retrospective shortly.",
    "Please observe the working agreement!",
    "We live agility, we don't just talk about it.",
    "Dot voting for the topics, please.",
    "We'll solve that in inspect and adapt.",
    "Scrum says a maximum of ten people in sprint planning.",
    "The burndown chart shows a trend.",
    "Team autonomy matters!",
    "Can we do ROTI? Return on time invested?",
    "I have a new icebreaker! Everyone names their favourite vegetable.",
    "The team needs a safe space. I'm ordering floor cushions.",
    "My retro format is 'sailboat'. Or 'starfish'. Or 'mad-sad-glad'. I can't decide.",
    "The impediment backlog now has more items than the product backlog.",
    "I did a three-day Scrum Master course. I know everything now.",
    "Who extended the daily to 45 minutes? Ah. That was me.",
    "Building trust in the team. Step one: trust fall. Nobody catches.",
    "I brought an exercise back from my coaching seminar. It only takes 90 minutes.",
    "The team needs more empowerment. I'm still clarifying what that means exactly.",
    "My new ritual: everyone says an affirmation each morning. Mandatory.",
    "We track a happiness metric now. The team is 'moderately happy'.",
    "I read a new book: 'Scrum for the Soul'. I recommend it to everyone.",
    "The team canvas is on the wall. Nobody has looked at it in six months.",
    "We're doing lean coffee now. No coffee, but lean.",
    "The retrospective concluded that we need fewer retrospectives.",
    "I facilitate the coffee breaks now too. Structure matters.",
    "The team has reached a maturity stage. I won't say which. It's stage one.",
    "We created a working agreement. Forty-seven points. All ignored.",
    "The velocity trend shows improvement! Or the estimates are getting worse.",
    "I created the Slack channel #scrum-feelings. Zero messages so far.",
    "My certificate now hangs framed above my desk. In gold.",
    "We're experimenting with mob programming. Eight people, one keyboard. Chaos.",
    "The team should self-organise. But precisely the way I want.",
    "I introduced a meditation before the standup. The first three fell asleep.",
    "The impediment board has its own impediment: it is full.",
    "My retro highlight: 'could we replace the Scrum Master?' Anonymous. Obviously.",
    "The AI estimated the story points. For everything: thirteen.",
    "I had the AI analyse the retro. Result: 'improve communication'. As always.",
    "We are AI-augmented agile now. The board still looks exactly the same.",
    "The bot summarised the daily. In eleven paragraphs.",
    "The AI suggests scrapping the retro. We'll discuss that in the retro.",
    "We have an impediment board now. The AI filled it. It is full.",
  ],
  [ColleagueRole.ProductOwner]: [
    "That's priority one, like the other 47 tickets.",
    "The business value is immense!",
    "A sprint goal is a sprint goal!",
    "The backlog is prioritised. In theory.",
    "We need more customer feedback.",
    "The story is ready, isn't it?",
    "Could we squeeze that into the sprint?",
    "The stakeholders want one more feature.",
    "The MVP has to be standing by next week.",
    "The user story is too big, we'll split it.",
    "Time to market is decisive.",
    "The acceptance criteria are clear. Basically.",
    "The ROI justifies the investment.",
    "Could we do a beta release?",
    "The feature requests are piling up.",
    "We're pivoting the strategy. Again.",
    "Epics, stories, tasks — all in one sprint?",
    "The roadmap has changed. Surprise.",
    "Think customer-centric, please.",
    "The competition already has that feature.",
    "The backlog has 500 items. The oldest are from 2021.",
    "I changed the priorities. Don't ask why.",
    "The story has no acceptance criteria. They're coming. Eventually.",
    "We need the feature yesterday. We'll ship the day after tomorrow.",
    "The stakeholder called. He wants everything different.",
    "We had a refinement? Oh, I was on the other call.",
    "My backlog is prioritised: everything is priority one. Problem solved.",
    "The stakeholder changed his mind. During the meeting. Twice.",
    "The roadmap runs to Q4. We're in Q1 and already three months behind.",
    "I rewrote the user story three times. It still says nothing.",
    "That feature has been carried over for eight sprints. It's our permanent ticket.",
    "We're pivoting. Again. Third pivot this year.",
    "The story is 100 story points. We don't estimate in story points? Fine, 100 days.",
    "The stakeholder wants a dashboard. About all the other dashboards.",
    "I ran the sprint demo. The only thing that worked was the greeting.",
    "The feature flag list has 200 entries. 180 are marked 'nobody knows'.",
    "Customer journey map? Got one. On a napkin. Does that count?",
    "We need discovery. Then delivery. Then discovery again. Ad infinitum.",
    "We have product-market fit. We say. To the investor.",
    "My OKR this quarter: 'do things'. Measurable? Well.",
    "The feature request came in at 23:47. By WhatsApp. From the CEO. To me personally.",
    "We're cancelling the feature. After six months of development. 'Strategic realignment'.",
    "The user interviews found that nobody wants what we're building. Great feedback otherwise.",
    "The product has three active users. Two of them are from our QA team.",
    "I facilitated the product discovery workshop. Four hours. Result: 'more data needed'.",
    "The investor asked about ARR. I said 'area rug revenue'. He laughed. I think.",
    "The AI generated forty user stories. Nobody wants any of them.",
    "The backlog was AI-prioritised. Top item is now a ticket from 2022.",
    "I typed 'MVP' and got an entire product back.",
    "The client writes his requirements with AI, we read them with AI. Somewhere in between there was a need.",
    "The persona is AI-generated. It has never bought anything.",
    "'What does the user want?' — The model says: everything.",
  ],
};
